import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { waLink, DEFAULT_WA_MESSAGE } from '../lib/whatsapp';
import { postLead } from '../lib/leads';

const TIME_SLOTS = ['10:00', '11:00', '12:00', '13:00', '16:00', '17:00', '18:00'];

function nextWeekdays(count = 14) {
  const out = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (out.length < count) {
    if (d.getDay() !== 0) out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}
const formatDate = (d) => d.toISOString().slice(0, 10);
const formatDateLabel = (d) => d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });

const INITIAL_FORM = { name: '', phone: '', email: '', modelo_interes: '', message: '' };

export default function AgendarModal() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const days = useMemo(() => nextWeekdays(14), []);
  const canSubmit = form.name && form.phone && form.email && date && time && consent && !busy;

  const close = useCallback(() => setOpen(false), []);
  const reset = useCallback(() => {
    setForm(INITIAL_FORM);
    setDate('');
    setTime('');
    setConsent(false);
    setDone(false);
    setError('');
  }, []);

  // Listen to global open event
  useEffect(() => {
    const handler = () => {
      reset();
      setOpen(true);
    };
    window.addEventListener('sm:open-agendar', handler);
    return () => window.removeEventListener('sm:open-agendar', handler);
  }, [reset]);

  // Body scroll lock + ESC to close
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      const res = await postLead('visit', {
        name: form.name,
        phone: form.phone,
        email: form.email,
        fecha_visita: date,
        hora_visita: time,
        modelo_interes: form.modelo_interes,
        message: form.message,
        source_page: window.location.pathname,
      });
      if (!res.ok) throw new Error('submit_failed');
      setDone(true);
    } catch {
      setError('Hubo un problema al enviar. Por favor inténtalo de nuevo o escríbenos por WhatsApp.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="agendar-modal" role="dialog" aria-modal="true" aria-label="Agenda tu visita" data-testid="agendar-modal">
      <button type="button" className="agendar-modal-backdrop" onClick={close} aria-label="Cerrar" />
      <div className="agendar-modal-card">
        <button type="button" className="agendar-modal-close" onClick={close} aria-label="Cerrar" data-testid="agendar-modal-close">
          <span className="icon-font">close</span>
        </button>

        <div className="agendar-grid">
          {/* IZQUIERDA */}
          <div className="agendar-left">
            <div className="text-label" style={{ color: 'rgba(255,255,255,0.65)' }}>Agenda tu visita</div>
            <h2 className="sm-h1 agendar-title">
              Conoce Santa <span className="serif-accent">Mónica</span> de cerca.
            </h2>
            <p className="agendar-lead">
              Te llevamos a recorrer el fraccionamiento, ver los modelos y resolver tus dudas con un asesor. Sin compromiso.
            </p>

            <ul className="agendar-includes">
              <li><span className="icon-font">check_circle</span> Recorrido por amenidades y áreas verdes</li>
              <li><span className="icon-font">check_circle</span> Visita a casa muestra Fernanda y Mariana</li>
              <li><span className="icon-font">check_circle</span> Asesoría de crédito (INFONAVIT, FOVISSSTE, bancos)</li>
              <li><span className="icon-font">check_circle</span> Sin presión, sin costo, ~45-60 min</li>
            </ul>

            <div className="agendar-alt">
              <span>¿Prefieres otro canal?</span>
              <div className="agendar-alt-buttons">
                <a href="tel:5567911796" className="agendar-alt-btn" data-testid="agendar-call">
                  <span className="icon-font">call</span>
                  Llámanos
                </a>
                <a href={waLink(DEFAULT_WA_MESSAGE)} target="_blank" rel="noopener noreferrer" className="agendar-alt-btn whatsapp" data-testid="agendar-whatsapp">
                  <img loading="lazy" src="/images/whatsapp.svg" alt="" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>

          {/* DERECHA: FORM */}
          <div className="agendar-right">
            {!done ? (
              <form className="agendar-form" onSubmit={submit} data-testid="agendar-form">
                <div className="agendar-form-head">
                  <strong>Reserva tu horario</strong>
                  <span>Un asesor te confirmará por WhatsApp en menos de 15 min · L-V 9-19h</span>
                </div>

                <label className="agendar-field">
                  <span>Nombre completo *</span>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="agendar-name" />
                </label>

                <div className="agendar-row">
                  <label className="agendar-field">
                    <span>WhatsApp *</span>
                    <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required placeholder="55 1234 5678" data-testid="agendar-phone" />
                  </label>
                  <label className="agendar-field">
                    <span>Correo *</span>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="tu@correo.com" data-testid="agendar-email" />
                  </label>
                </div>

                <label className="agendar-field">
                  <span>Modelo de interés</span>
                  <select value={form.modelo_interes} onChange={(e) => setForm({ ...form, modelo_interes: e.target.value })} data-testid="agendar-modelo">
                    <option value="">Aún no lo sé</option>
                    <option value="Fernanda">Fernanda</option>
                    <option value="Mariana">Mariana</option>
                    <option value="Ambos">Quiero ver ambas</option>
                  </select>
                </label>

                <div className="agendar-field">
                  <span>¿Qué día te conviene? *</span>
                  <div className="agendar-dates" role="radiogroup" aria-label="Día">
                    {days.slice(0, 8).map((d) => {
                      const v = formatDate(d);
                      return (
                        <button type="button" key={v} className={`agendar-chip${date === v ? ' active' : ''}`} onClick={() => setDate(v)} data-testid={`agendar-date-${v}`}>
                          {formatDateLabel(d)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="agendar-field">
                  <span>Horario preferido *</span>
                  <div className="agendar-times" role="radiogroup" aria-label="Hora">
                    {TIME_SLOTS.map((t) => (
                      <button type="button" key={t} className={`agendar-chip${time === t ? ' active' : ''}`} onClick={() => setTime(t)} data-testid={`agendar-time-${t}`}>
                        {t} h
                      </button>
                    ))}
                  </div>
                </div>

                <label className="agendar-field">
                  <span>Mensaje (opcional)</span>
                  <textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="¿Tienes alguna duda específica?" data-testid="agendar-message" />
                </label>

                <label className="agendar-consent">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="agendar-consent" />
                  <span>
                    Acepto el <a href="/aviso-de-privacidad" target="_blank" rel="noopener noreferrer">aviso de privacidad</a> y autorizo el contacto vía WhatsApp/llamada/correo.
                  </span>
                </label>

                {error && <div className="agendar-error" role="alert">{error}</div>}

                <button type="submit" disabled={!canSubmit} className="agendar-submit" data-testid="agendar-submit">
                  {busy ? 'Enviando…' : 'Solicitar mi visita'}
                  <span className="icon-font">arrow_forward</span>
                </button>

                <p className="agendar-disclaim">
                  Tu solicitud es <strong>tentativa</strong>. Un asesor humano te confirma el horario final por WhatsApp.
                </p>
              </form>
            ) : (
              <div className="agendar-success" data-testid="agendar-success">
                <div className="agendar-success-icon"><span className="icon-font">check_circle</span></div>
                <h3>¡Listo, {form.name.split(' ')[0]}!</h3>
                <p>Recibimos tu solicitud para el <strong>{date}</strong> a las <strong>{time} h</strong>.</p>
                <p>Un asesor te confirma por WhatsApp en menos de 15 min en horario L-V 9-19h.</p>
                <a href={waLink(`Hola, acabo de solicitar una visita para el ${date} a las ${time}. Mi nombre es ${form.name}.`)} target="_blank" rel="noopener noreferrer" className="agendar-success-cta" data-testid="agendar-success-wa">
                  <img loading="lazy" src="/images/whatsapp.svg" alt="" /> Confirmar más rápido por WhatsApp
                </a>
                <button type="button" className="agendar-success-close" onClick={close}>Cerrar</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function openAgendarModal() {
  window.dispatchEvent(new CustomEvent('sm:open-agendar'));
}
